/**
 * Google Data Source - v13 Section 6.10 (Sprint 11g)
 *
 * Direct fetcher for Google's Generative AI model list.
 * These are the REAL model IDs that the Google Generative AI API accepts.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  NO HARDCODED MODEL DATA - All model info comes from Google API              ║
 * ║                                                                              ║
 * ║  Google API provides: model name, display_name, inputTokenLimit,             ║
 * ║                       outputTokenLimit, supportedGenerationMethods           ║
 * ║  Google API does NOT provide: pricing                                        ║
 * ║  Pricing should be enriched from OpenRouter metadata                         ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * Note: Google API provides inputTokenLimit and outputTokenLimit which map to:
 *   - inputTokenLimit → contextWindow
 *   - outputTokenLimit → maxCompletionTokens
 *
 * @see https://ai.google.dev/api/models
 */
/**
 * Extract model ID from full resource name
 * "models/gemini-2.0-flash" → "gemini-2.0-flash"
 */
const extractModelId = (name) => {
    const parts = name.split('/');
    return parts[parts.length - 1];
};
/**
 * Create display name from model data
 * Prefer API-provided displayName, fall back to generic cleanup
 */
const createDisplayName = (model) => {
    if (model.displayName) {
        return model.displayName;
    }
    // Fallback: generic cleanup
    const modelId = extractModelId(model.name);
    return modelId
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
/**
 * Filter for chat/content generation models
 * Uses supportedGenerationMethods from API - TYPE-based filtering
 */
const isChatModel = (model) => {
    const methods = model.supportedGenerationMethods ?? [];
    // Include models that support content generation (generateContent)
    // Exclude models that only support embedContent or other non-chat methods
    return methods.includes('generateContent');
};
/**
 * Detect if model is a reasoning model based on model characteristics
 */
const isReasoningModel = (modelId) => {
    const id = modelId.toLowerCase();
    // Google's reasoning models (if any) - currently none identified
    // This can be updated when Google releases reasoning models
    if (id.includes('thinking') || id.includes('reason')) {
        return true;
    }
    return null; // Unknown
};
/**
 * Fetch models directly from Google Generative AI API
 * Returns API-provided data including token limits
 *
 * @param apiKey - Google API key
 * @returns Array of ModelMetadata for Google models
 */
export const fetchFromGoogle = async (apiKey) => {
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`Google API returned ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.models || !Array.isArray(data.models)) {
        throw new Error('Invalid response format from Google API');
    }
    const models = [];
    for (const model of data.models) {
        // Skip non-chat models (method-based filtering from API)
        if (!isChatModel(model))
            continue;
        const modelId = extractModelId(model.name);
        // Use API-provided data - Google provides token limits!
        models.push({
            id: modelId,
            provider: 'google',
            displayName: createDisplayName(model),
            // Google API provides these - map to our field names
            contextWindow: model.inputTokenLimit ?? null,
            maxCompletionTokens: model.outputTokenLimit ?? null,
            pricing: null, // API doesn't provide pricing - enrich from OpenRouter
            capabilities: model.supportedGenerationMethods ?? [],
            zeroDataRetention: null, // Unknown for Google
            isReasoningModel: isReasoningModel(modelId),
        });
    }
    return models;
};
